import { useEffect, useState, useCallback } from "react";
import { api, fmtDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { ArrowsClockwiseIcon as ArrowsClockwise, CircleIcon as Circle, WifiHighIcon as WifiHigh, WarningCircleIcon as WarningCircle, MagnifyingGlassIcon as MagnifyingGlass, HardDrivesIcon as HardDrives, ShieldCheckIcon as ShieldCheck } from "@phosphor-icons/react";

const CAT_LABEL = { ap: "Access Point", sw: "Switch", gw: "Gateway / Firewall", other: "Altro" };
const CAT_ICON = { ap: WifiHigh, sw: HardDrives, gw: ShieldCheck, other: HardDrives };

export default function ZyxelDevices() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [status, setStatus] = useState(null);
  const [orgs, setOrgs] = useState([]);
  const [devices, setDevices] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [q, setQ] = useState("");
  const [fOrg, setFOrg] = useState("all");
  const [fCat, setFCat] = useState("all");
  const [fStato, setFStato] = useState("all");
  const [detail, setDetail] = useState(null); // {device, data, loading}

  const loadStatus = useCallback(() => api.get("/zyxel/status").then(({ data }) => setStatus(data)).catch(() => {}), []);
  const loadDevices = useCallback(() => {
    const params = {};
    if (q) params.q = q;
    if (fOrg !== "all") params.organization_id = fOrg;
    if (fCat !== "all") params.category = fCat;
    if (fStato !== "all") params.stato = fStato;
    api.get("/zyxel/devices", { params }).then(({ data }) => setDevices(data)).catch(() => setDevices([]));
  }, [q, fOrg, fCat, fStato]);

  useEffect(() => { loadStatus(); api.get("/zyxel/organizations").then(({ data }) => setOrgs(data.filter((o) => o.api_enabled))).catch(() => {}); }, [loadStatus]);
  useEffect(() => { loadDevices(); }, [loadDevices]);

  const pollStatus = useCallback(() => {
    let tries = 0;
    const iv = setInterval(async () => {
      tries += 1;
      try {
        const { data } = await api.get("/zyxel/status");
        setStatus(data);
        if (data.status !== "running" || tries > 120) {
          clearInterval(iv); setSyncing(false);
          if (data.status === "error") toast.error("Sync fallita: " + (data.error || "errore"));
          else if (data.last_result) toast.success(`Sync completata · ${data.last_result.dispositivi} dispositivi, ${data.last_result.siti} siti`);
          loadDevices();
        }
      } catch { /* continua */ }
    }, 3000);
  }, [loadDevices]);

  const sync = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/zyxel/sync");
      if (data.configured === false) { toast.error(data.message); setSyncing(false); return; }
      if (data.running) toast.info("Sincronizzazione già in corso");
      else toast.success("Sincronizzazione avviata in background…");
      pollStatus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore durante l'avvio della sincronizzazione");
      setSyncing(false);
    }
  };

  useEffect(() => { if (status?.status === "running" && !syncing) { setSyncing(true); pollStatus(); } }, [status?.status]); // eslint-disable-line

  const openDetail = async (d) => {
    setDetail({ device: d, data: null, loading: true });
    try {
      const { data } = await api.get(`/zyxel/devices/${d.zyxel_id}/detail`);
      setDetail({ device: d, data, loading: false });
    } catch (e) {
      setDetail({ device: d, data: null, loading: false, error: e?.response?.data?.detail || "Errore" });
    }
  };

  const toggleVisibility = async (d, val) => {
    try {
      await api.patch(`/zyxel/devices/${d.zyxel_id}/visibility`, { visibile_area_cliente: val });
      setDevices((prev) => prev.map((x) => x.zyxel_id === d.zyxel_id ? { ...x, visibile_area_cliente: val } : x));
      if (detail?.device?.zyxel_id === d.zyxel_id) setDetail((dt) => ({ ...dt, device: { ...dt.device, visibile_area_cliente: val } }));
      toast.success(val ? "Visibile nell'area cliente" : "Nascosto dall'area cliente");
    } catch { toast.error("Errore aggiornamento visibilità"); }
  };

  if (status && !status.configured) {
    return (
      <Card className="rounded-sm border-border shadow-none p-8 text-center" data-testid="zyxel-not-configured">
        <WarningCircle size={36} className="text-amber-500 mx-auto mb-3" />
        <h3 className="font-heading font-semibold mb-1">Zyxel Nebula non configurato</h3>
        <p className="text-sm text-muted-foreground">Imposta la API Key in <b>Impostazioni → Integrazioni API → Zyxel Nebula</b>, poi sincronizza i dispositivi.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="zyxel-devices">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-sm text-muted-foreground">
          {status?.device_count != null && <span data-testid="zyxel-device-count">{status.device_count} dispositivi</span>}
          {status?.site_count != null && <span> · {status.site_count} siti · {orgs.length} org. abilitate</span>}
          {status?.last_sync && <span> · ultima sync {fmtDate(status.last_sync)}</span>}
        </div>
        <Button onClick={sync} disabled={syncing} className="rounded-sm gap-2" data-testid="zyxel-sync-btn">
          <ArrowsClockwise size={16} className={syncing ? "animate-spin" : ""} /> {syncing ? "Sincronizzazione…" : "Sincronizza ora"}
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 items-center">
        <div className="relative">
          <MagnifyingGlass size={15} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca nome / modello / MAC / org…" className="pl-8 rounded-sm bg-white w-72" data-testid="zyxel-search" />
        </div>
        <Select value={fOrg} onValueChange={setFOrg}>
          <SelectTrigger className="w-56 rounded-sm bg-white" data-testid="zyxel-filter-org"><SelectValue placeholder="Organizzazione" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le organizzazioni</SelectItem>
            {orgs.map((o) => <SelectItem key={o.zyxel_org_id} value={o.zyxel_org_id}>{o.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={fCat} onValueChange={setFCat}>
          <SelectTrigger className="w-44 rounded-sm bg-white" data-testid="zyxel-filter-cat"><SelectValue placeholder="Tipo" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i tipi</SelectItem>
            <SelectItem value="ap">Access Point</SelectItem>
            <SelectItem value="sw">Switch</SelectItem>
            <SelectItem value="gw">Gateway / Firewall</SelectItem>
          </SelectContent>
        </Select>
        <Select value={fStato} onValueChange={setFStato}>
          <SelectTrigger className="w-36 rounded-sm bg-white" data-testid="zyxel-filter-stato"><SelectValue placeholder="Stato" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli stati</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="offline">Offline</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-sm text-muted-foreground ml-auto" data-testid="zyxel-count">{(devices || []).length} dispositivi</span>
      </div>

      <Card className="rounded-sm border-border shadow-none">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Dispositivo</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>Organizzazione / Sito</TableHead>
              <TableHead>Firmware</TableHead>
              <TableHead>Stato</TableHead>
              {isAdmin && <TableHead className="text-center">Area cliente</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {devices === null && <TableRow><TableCell colSpan={isAdmin ? 6 : 5} className="text-center text-muted-foreground py-10">Caricamento…</TableCell></TableRow>}
            {devices?.length === 0 && <TableRow><TableCell colSpan={isAdmin ? 6 : 5} className="text-center text-muted-foreground py-10">Nessun dispositivo. Premi "Sincronizza ora" per importarli da Zyxel Nebula.</TableCell></TableRow>}
            {devices?.map((d) => {
              const Icon = CAT_ICON[d.category] || HardDrives;
              return (
                <TableRow key={d.zyxel_id} data-testid="zyxel-device-row" className="hover:bg-secondary cursor-pointer" onClick={() => openDetail(d)}>
                  <TableCell className="font-medium">
                    <div className="flex items-center gap-2"><Icon size={15} className="text-muted-foreground" /> {d.name || d.model || "—"}</div>
                    <div className="text-xs text-muted-foreground pl-6">{d.model}{d.mac ? ` · ${d.mac}` : ""}</div>
                  </TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm">{CAT_LABEL[d.category] || d.type || "—"}</Badge></TableCell>
                  <TableCell className="text-sm">
                    <div>{d.org_name || "—"}</div>
                    <div className="text-xs text-muted-foreground">{d.site_name || "—"}</div>
                  </TableCell>
                  <TableCell className="text-sm">
                    {d.firmware || "—"}
                    {d.firmware_status === "UPGRADE_AVAILABLE" && <Badge className="ml-1.5 rounded-sm bg-amber-100 text-amber-700 text-[10px]">Aggiornabile</Badge>}
                  </TableCell>
                  <TableCell>
                    <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${d.online ? "text-emerald-600" : "text-slate-500"}`} data-testid={`zyxel-stato-${d.zyxel_id}`}>
                      <Circle size={9} weight="fill" /> {d.online ? "Online" : "Offline"}
                    </span>
                  </TableCell>
                  {isAdmin && (
                    <TableCell className="text-center" onClick={(e) => e.stopPropagation()}>
                      <Switch checked={!!d.visibile_area_cliente} onCheckedChange={(v) => toggleVisibility(d, v)} data-testid={`zyxel-visibility-${d.zyxel_id}`} />
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      <ZyxelDetailDialog detail={detail} onClose={() => setDetail(null)} isAdmin={isAdmin} onToggleVisibility={toggleVisibility} />
    </div>
  );
}

function ZyxelDetailDialog({ detail, onClose, isAdmin, onToggleVisibility }) {
  if (!detail) return null;
  const d = detail.device;
  const data = detail.data;
  const ports = data?.ports;
  const poe = Array.isArray(data?.poe) ? data.poe[0] : data?.poe;
  const poeMap = {};
  (poe?.ports || []).forEach((p) => { poeMap[p.portNum] = p; });
  const clients = data?.clients;
  const apIp = Array.isArray(data?.system) ? data.system : null;

  return (
    <Dialog open={!!detail} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="rounded-sm max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="zyxel-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            {d.name || d.model}
            <span className={`inline-flex items-center gap-1 text-xs font-medium ${d.online ? "text-emerald-600" : "text-slate-500"}`}><Circle size={8} weight="fill" /> {d.online ? "Online" : "Offline"}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-sm border-b pb-3">
          <Info label="Modello" value={d.model} />
          <Info label="Tipo" value={CAT_LABEL[d.category] || d.type} />
          <Info label="Serial" value={d.serial} mono />
          <Info label="MAC" value={d.mac} mono />
          <Info label="Organizzazione" value={d.org_name} />
          <Info label="Sito" value={d.site_name} />
          <Info label="Firmware" value={`${d.firmware || "—"} (${d.firmware_status || "—"})`} />
          {d.product_info && <Info label="Prodotto" value={d.product_info} />}
        </div>

        {isAdmin && (
          <div className="flex items-center justify-between bg-secondary/50 rounded-sm px-3 py-2">
            <span className="text-sm">Visibile nell'area cliente</span>
            <Switch checked={!!d.visibile_area_cliente} onCheckedChange={(v) => onToggleVisibility(d, v)} data-testid="zyxel-detail-visibility" />
          </div>
        )}

        {detail.loading && <p className="text-sm text-muted-foreground py-6 text-center">Caricamento dettagli live…</p>}
        {detail.error && <p className="text-sm text-destructive py-4">{detail.error}</p>}

        {data && (
          <Tabs defaultValue={ports ? "ports" : (clients ? "clients" : "firmware")} className="mt-1">
            <TabsList className="rounded-sm">
              {ports && <TabsTrigger value="ports" data-testid="zyxel-tab-ports">Porte</TabsTrigger>}
              {clients && <TabsTrigger value="clients" data-testid="zyxel-tab-clients">Client</TabsTrigger>}
              {apIp && <TabsTrigger value="ap" data-testid="zyxel-tab-ap">Rete</TabsTrigger>}
              <TabsTrigger value="firmware" data-testid="zyxel-tab-firmware">Firmware</TabsTrigger>
            </TabsList>

            {ports && (
              <TabsContent value="ports">
                <Table>
                  <TableHeader><TableRow><TableHead>Porta</TableHead><TableHead>Velocità</TableHead>{d.category === "sw" && <><TableHead>PoE</TableHead><TableHead>Consumo</TableHead></>}</TableRow></TableHeader>
                  <TableBody>
                    {ports.map((p) => (
                      <TableRow key={p.portNum}>
                        <TableCell className="font-mono">{p.portNum}</TableCell>
                        <TableCell><span className={p.linkSpeed === "OFFLINE" ? "text-slate-400" : "text-emerald-600"}>{(p.linkSpeed || "").replace("AUTO_", "").replace("_", " ") || "—"}</span></TableCell>
                        {d.category === "sw" && <><TableCell>{poeMap[p.portNum]?.enabled ? <Badge className="rounded-sm bg-emerald-100 text-emerald-700 text-[10px]">On</Badge> : <span className="text-slate-400 text-xs">Off</span>}</TableCell>
                        <TableCell className="tabular-nums text-xs">{poeMap[p.portNum]?.usage ? `${poeMap[p.portNum].usage.toFixed(1)} W` : "—"}</TableCell></>}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
                {poe?.budget != null && <p className="text-xs text-muted-foreground mt-2">Budget PoE: {poe.currentUsage?.toFixed(1)} / {poe.budget?.toFixed(1)} W</p>}
              </TabsContent>
            )}

            {clients && (
              <TabsContent value="clients">
                {clients.length === 0 ? <p className="text-sm text-muted-foreground py-4">Nessun client connesso.</p> : (
                  <Table>
                    <TableHeader><TableRow><TableHead>Descrizione / Host</TableHead><TableHead>IP</TableHead><TableHead>MAC</TableHead><TableHead>VLAN</TableHead></TableRow></TableHeader>
                    <TableBody>
                      {clients.slice(0, 100).map((c, i) => (
                        <TableRow key={i}>
                          <TableCell>{c.description || c.osHostname || "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{c.ipv4Address || "—"}</TableCell>
                          <TableCell className="font-mono text-xs">{c.macAddress || "—"}</TableCell>
                          <TableCell>{c.vlan ?? "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </TabsContent>
            )}

            {apIp && (
              <TabsContent value="ap">
                <Table>
                  <TableHeader><TableRow><TableHead>Interfaccia</TableHead><TableHead>IP</TableHead><TableHead>Gateway</TableHead><TableHead>VLAN</TableHead></TableRow></TableHeader>
                  <TableBody>
                    {apIp.map((it, i) => (
                      <TableRow key={i}>
                        <TableCell>{it.interface || "—"}</TableCell>
                        <TableCell className="font-mono text-xs">{it.ipv4Address || "—"}{it.ipv4Type ? ` (${it.ipv4Type})` : ""}</TableCell>
                        <TableCell className="font-mono text-xs">{it.ipv4Gateway || "—"}</TableCell>
                        <TableCell>{it.vlan ?? "—"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TabsContent>
            )}

            <TabsContent value="firmware">
              {data.firmware ? (
                <div className="grid grid-cols-2 gap-y-1.5 text-sm py-2">
                  <Info label="Versione attuale" value={data.firmware.currentVersion} />
                  <Info label="Ultima disponibile" value={data.firmware.latestVersion} />
                  <Info label="Stato" value={data.firmware.status} />
                  <Info label="Ultimo aggiornamento" value={data.firmware.lastUpgradeTime ? fmtDate(data.firmware.lastUpgradeTime) : "—"} />
                </div>
              ) : <p className="text-sm text-muted-foreground py-4">Info firmware non disponibili.</p>}
            </TabsContent>
          </Tabs>
        )}

        {data?.errors && Object.keys(data.errors).length > 0 && (
          <p className="text-xs text-amber-600 mt-2">Alcuni dati non disponibili: {Object.keys(data.errors).join(", ")}</p>
        )}
      </DialogContent>
    </Dialog>
  );
}

function Info({ label, value, mono }) {
  return (
    <div>
      <span className="text-xs uppercase tracking-wider text-muted-foreground">{label}</span>
      <div className={mono ? "font-mono text-xs" : ""}>{value || "—"}</div>
    </div>
  );
}
